import {
  EVENT_CATALOGUE,
  EVENT_CODES,
  EVENT_SEVERITIES,
  EVENT_SEVERITY,
  eventCodeSchema,
  eventSeveritySchema,
  parisDayBounds,
  parisDayKey,
  statusLabel,
  type EventCode,
  type EventSeverity,
} from '@flux/shared';
import { ListFilter } from 'lucide-react';
import type { JSX } from 'react';
import { z } from 'zod';
import { OriginFilter } from '@/components/filters/origin-filter.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { Input } from '@/components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';

const ALL_CODES_VALUE = '__all__';

export interface JournalFiltersValue {
  severity: EventSeverity[];
  code: EventCode[];
  technical: boolean;
  search?: string;
  from?: string;
  to?: string;
}

export type JournalFiltersPatch = Partial<{
  severity: EventSeverity[] | undefined;
  code: EventCode[] | undefined;
  technical: boolean | undefined;
  search: string | undefined;
  from: string | undefined;
  to: string | undefined;
}>;

function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/** Lit les filtres de l'écran Journal depuis `useSearch({strict:false})` (`02` §11). */
export function readJournalFilters(rawSearch: unknown): JournalFiltersValue {
  const search = readRecord(rawSearch);
  const severity = z.array(eventSeveritySchema).safeParse(search.severity);
  const code = z.array(eventCodeSchema).safeParse(search.code);
  return {
    severity: severity.success ? severity.data : [],
    code: code.success ? code.data : [],
    technical: search.technical === true,
    ...(typeof search.search === 'string' ? { search: search.search } : {}),
    ...(typeof search.from === 'string' ? { from: search.from } : {}),
    ...(typeof search.to === 'string' ? { to: search.to } : {}),
  };
}

export interface JournalFiltersProps {
  value: JournalFiltersValue;
  onChange: (patch: JournalFiltersPatch) => void;
  origin: string | null;
  onOriginChange: (next: string | null) => void;
  /** Onglet courant : la sévérité n'a de sens que dans Alertes, la case « techniques » que dans Journal. */
  view: 'journal' | 'alertes';
}

/**
 * Filtres de l'écran Journal (`02` §11) — rendus dans la seconde ligne de
 * `ListToolbar`, la recherche étant portée par la première.
 *
 * `from`/`to` sont stockés dans l'URL en ISO-8601 complet
 * (`eventListQuerySchema.from/to`, `isoDateTimeSchema`) : la conversion
 * jour → bornes Paris se fait ici, à la saisie (`parisDayBounds`).
 */
export function JournalFilters({
  value,
  onChange,
  origin,
  onOriginChange,
  view,
}: JournalFiltersProps): JSX.Element {
  const fromDay = value.from ? parisDayKey(new Date(value.from)) : '';
  const toDay = value.to ? parisDayKey(new Date(value.to)) : '';

  return (
    <>
      <div className="flex items-center gap-1.5">
        <Input
          type="date"
          aria-label="Depuis le"
          className="w-40"
          value={fromDay}
          onChange={(event) => {
            const raw = event.target.value;
            onChange({ from: raw ? parisDayBounds(raw).start.toISOString() : undefined });
          }}
        />
        <span className="text-sm text-muted-foreground" aria-hidden="true">
          →
        </span>
        <Input
          type="date"
          aria-label="Jusqu'au"
          className="w-40"
          value={toDay}
          onChange={(event) => {
            const raw = event.target.value;
            onChange({ to: raw ? parisDayBounds(raw).end.toISOString() : undefined });
          }}
        />
      </div>

      {view === 'alertes' ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" aria-label="Filtrer par sévérité">
              <ListFilter aria-hidden="true" />
              Sévérité{value.severity.length > 0 ? ` (${String(value.severity.length)})` : ''}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {EVENT_SEVERITIES.filter((code) => code !== 'info').map((code) => (
              <DropdownMenuCheckboxItem
                key={code}
                checked={value.severity.includes(code)}
                onCheckedChange={(checked) => {
                  const next = checked
                    ? [...value.severity, code]
                    : value.severity.filter((entry) => entry !== code);
                  onChange({ severity: next.length > 0 ? next : undefined });
                }}
              >
                {statusLabel(EVENT_SEVERITY, code)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <OriginFilter value={origin} onChange={onOriginChange} includePennylane />

      <Select
        value={value.code[0] ?? ALL_CODES_VALUE}
        onValueChange={(next) => {
          onChange({ code: next === ALL_CODES_VALUE ? undefined : [eventCodeSchema.parse(next)] });
        }}
      >
        <SelectTrigger className="w-64" aria-label="Événement">
          <SelectValue placeholder="Tous les événements" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_CODES_VALUE}>Tous les événements</SelectItem>
          {EVENT_CODES.map((code) => (
            <SelectItem key={code} value={code}>
              {EVENT_CATALOGUE[code].titleFr}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {view === 'journal' ? (
        <label className="flex h-9 cursor-pointer select-none items-center gap-2 text-sm text-foreground">
          <Checkbox
            checked={value.technical}
            onCheckedChange={(checked) => {
              onChange({ technical: checked === true ? true : undefined });
            }}
          />
          Afficher les événements techniques
        </label>
      ) : null}
    </>
  );
}

/** `true` dès qu'un filtre (hors recherche) est posé — pilote « Réinitialiser ». */
export function hasActiveJournalFilters(
  value: JournalFiltersValue,
  origin: string | null,
): boolean {
  return (
    value.severity.length > 0 ||
    value.code.length > 0 ||
    value.technical ||
    value.from !== undefined ||
    value.to !== undefined ||
    origin !== null
  );
}
